import { useInternational } from '@/common/locale';
import { getCurrentTheme } from '@/common/utils/commonUtils';
import { copyTextToClipboard } from '@/common/utils/browser';
import { convertNanoToFomattedTime } from '@/common/utils/time.utils';
import { useCodeMirror } from '@/tool/components/codeEditor/codeEditor.setup';
import CodeEditor from '@/tool/components/codeEditor/CodeEditor.vue';
import { lineHighlightField } from '@/tool/components/codeEditor/extenstions/lineHighlight';
import { searchExtensions } from '@/tool/components/codeEditor/extenstions/search';
import { debounce, isEmpty } from 'lodash-es';
import { computed, CSSProperties, getCurrentInstance, nextTick, ref, watch } from 'vue';
import { ContainerLogLine, LOG_POS, LogPosType } from '../podsListDetail/types';
import { PodContainerLogDetailRequest } from '../podsListDetail/containerLogBox.setup';

export const useCopyToClipboard = () => {
  const ctx = getCurrentInstance()!.appContext.config.globalProperties;

  const copyToClipboard = async (text: string) => {
    await copyTextToClipboard(ctx, text);
  };

  return {
    copyToClipboard,
  };
};

export interface Props {
  log: ContainerLogLine[];
  isFetching?: boolean;
  placeholder?: string;
  previous?: boolean;
  logType?: LogPosType;
  height?: CSSProperties['height'];
  useExternalBtn?: boolean;
  focusLine?: number;
  displayLogTime?: boolean;
  displayLogTitle?: boolean;
  isCurrentLog?: boolean;
}

export interface Emits {
  (e: 'copy:log', log: string): void;
  (e: 'open:external', focusedLine?: number): void;
  (e: 'get:ijeon', value: Omit<PodContainerLogDetailRequest, 'lineCount'>): void;
  (e: 'get:ihu', value: Omit<PodContainerLogDetailRequest, 'lineCount'>): void;
}

interface LineNumRecorder {
  lines: number[];
  newLineNum: number;
  oldLineNum: number;
}
const useLineNumRecorder = () => {
  const addToTop = ({ lines, newLineNum, oldLineNum }: LineNumRecorder) => {
    return [...lines, 1].map((l) => l + newLineNum - oldLineNum);
  };
  const addToBottom = ({ lines, oldLineNum }: LineNumRecorder) => {
    return [...lines, oldLineNum];
  };

  return {
    addToTop,
    addToBottom,
  };
};

export const setup = (props: Props, emit: Emits) => {
  const { copyToClipboard } = useCopyToClipboard();

  const { t } = useInternational();
  const theme = getCurrentTheme();
  const { codeEditorOptions: options } = useCodeMirror({
    theme,
    extensions: [...lineHighlightField(), ...searchExtensions()],
    options: {
      disabled: false,
      autofocus: false,
      readonly: true,
      showLineNumbers: true,
      showActiveCurrentLine: false,
      placeholder: props.placeholder || t('MESSAGE.NO_DATA', 'No previous terminated logs.'),
    },
  });

  const showMore = ref<boolean>(props.log.length <= 12 || false);
  const maxHeight = computed(() => (showMore.value === false ? '' : props.height));
  const overflow = computed(() => (!showMore.value ? 'hidden' : 'auto'));
  const logTitle = computed(() => {
    if (props.displayLogTitle === false) return '';
    return props.previous ? 'Previous terminated' : 'Current Log';
  });

  const logFormatter = computed(() => {
    if (props.displayLogTime) {
      return (line: ContainerLogLine) =>
        `${convertNanoToFomattedTime(line.timestamp)}: ${line.log}`;
    }
    return (line: ContainerLogLine) => line.log;
  });

  const visibleLog = computed(() => {
    if (isEmpty(props.log) || (props.log.length === 1 && props.log[0].log === '')) return '';

    if (!showMore.value && props.logType === LOG_POS.head)
      return props.log.slice(0, 12).map(logFormatter.value).join('\n');
    if (!showMore.value && props.logType === LOG_POS.tail)
      return props.log.slice(-12).map(logFormatter.value).join('\n');

    const formattedLogs = props.log.map(logFormatter.value);
    const logText = formattedLogs.join('\n');
    const lineCount = logText.split('\n').length;

    if (lineCount < 12) {
      const logs = [...formattedLogs];
      for (let i = lineCount; i < 12; i++) {
        logs.push('');
      }
      return logs.join('\n');
    }
    return logText;
  });

  const copyableLog = computed(() => {
    if (isEmpty(props.log) || (props.log.length === 1 && props.log[0].log === '')) return '';

    if (!showMore.value && props.logType === LOG_POS.head)
      return props.log.slice(0, 12).map(logFormatter.value).join('\n');
    if (!showMore.value && props.logType === LOG_POS.tail)
      return props.log.slice(-12).map(logFormatter.value).join('\n');

    return props.log.map(logFormatter.value).join('\n');
  });

  const contentView = ref<HTMLDivElement | null>(null);

  const onClickIjeon = debounce(() => {
    if (!showMore.value) return;

    if (isEmpty(props.log)) return;
    emit('get:ijeon', {
      sinceTime: props.log[0].timestamp,
      position: props.isCurrentLog ? (props.logType ?? LOG_POS.tail) : LOG_POS.tail,
      previous: props.previous ?? false,
    });
  }, 300);

  const onClickIhu = debounce(() => {
    if (!showMore.value) return;

    if (isEmpty(props.log)) return;
    emit('get:ihu', {
      sinceTime: props.log[props.log.length - 1].timestamp,
      position: props.isCurrentLog ? (props.logType ?? LOG_POS.head) : LOG_POS.head,
      previous: props.previous ?? false,
    });
  }, 300);
  const viewerRef = ref<InstanceType<typeof CodeEditor>>();
  let lines: number[] = [];
  const { addToBottom, addToTop } = useLineNumRecorder();

  const onClickExternal = () => {
    emit('open:external', viewerRef.value?.getFocusedLine());
  };

  watch(showMore, (_showMore) => {
    if (_showMore && props.logType === LOG_POS.tail) {
      nextTick(() => {
        if (contentView.value) contentView.value.scrollTo(0, contentView.value.scrollHeight);
      });
    }
  });

  watch(
    () => [viewerRef.value, contentView.value] as const,
    ([editor, view]) => {
      if (editor && props.focusLine && view) {
        editor.lineHighlight([props.focusLine]);
        editor.scrollInto(props.focusLine);
      }
    },
  );

  watch(
    () => props.logType,
    (newType, oldType) => {
      if (newType === oldType || !props.isCurrentLog) return;
      lines = [];
    },
  );

  watch(
    () => props.log,
    async (newLogs, oldLogs) => {
      if (!showMore.value) {
        showMore.value = newLogs.length <= 12 || false;
        return;
      }

      let prevScrollHeight: number = 0;
      let prevScrollTop: number = 0;

      if (newLogs.length < oldLogs.length) lines = [];
      if (props.logType === LOG_POS.tail && contentView.value) {
        prevScrollHeight = contentView.value.scrollHeight;
        prevScrollTop = contentView.value.scrollTop;
      }
      await nextTick();
      if (!contentView.value || newLogs.length <= oldLogs.length || oldLogs.length === 0) return;

      if (props.logType === LOG_POS.head) {
        lines = addToBottom({ lines, newLineNum: newLogs.length, oldLineNum: oldLogs.length });
      }
      if (props.logType === LOG_POS.tail) {
        contentView.value.scrollTop =
          prevScrollTop + contentView.value.scrollHeight - prevScrollHeight;

        lines = addToTop({ lines, newLineNum: newLogs.length, oldLineNum: oldLogs.length });
      }
      viewerRef.value?.lineHighlight(lines);
    },
  );

  return {
    viewerRef,

    onClickIjeon,
    onClickIhu,
    copyToClipboard,
    onClickExternal,
    options,
    visibleLog,
    copyableLog,
    maxHeight,
    logTitle,

    showMore,
    overflow,
    contentView,
  };
};
